/* ==========================================================
   J9 Marketplace Strip — assets/j9-marketplaces.js
   Seamless marquee built for a SMALL logo set:
   - fills the screen with as many copies as needed
   - shuffles platform order on every copy (text items keep their slots)
   - never puts the same logo next to itself, even across the loop seam
   - static row when logos fit (motion = "fit") or motion is off
   ========================================================== */
(function () {
  'use strict';
  if (!window.customElements || customElements.get('j9-marketplaces')) return;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Small seeded random generator → same order on every page view (no layout jumps)
  function seeded(seed) {
    return function () {
      seed = (seed + 0x6d2b79f5) | 0;
      var t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function shuffled(list, rand) {
    var a = list.slice();
    for (var i = a.length - 1; i > 0; i--) {
      var j = Math.floor(rand() * (i + 1));
      var t = a[i]; a[i] = a[j]; a[j] = t;
    }
    return a;
  }

  function isPlatform(node) { return node.hasAttribute('data-platform'); }
  function keyOf(node) { return node.getAttribute('data-key'); }

  function platformKeys(seq) {
    return seq.filter(isPlatform).map(keyOf);
  }

  function overlap(a, b) {
    for (var i = 0; i < a.length; i++) if (b.indexOf(a[i]) !== -1) return true;
    return false;
  }

  // Shuffle only the platforms; text items stay where the merchant placed them.
  // A logo may not reappear within `win` logos across the join with the previous copy
  // (avoidStart) or across the loop seam back to the very first copy (avoidEnd).
  function permute(source, rand, avoidStart, avoidEnd, win) {
    var platforms = source.filter(isPlatform);
    var best = null, bestScore = Infinity;
    for (var tries = 0; tries < 60; tries++) {
      var order = shuffled(platforms, rand);
      var keys = order.map(keyOf);
      var score = 0;
      if (win && avoidStart.length && overlap(keys.slice(0, win), avoidStart)) score++;
      if (win && avoidEnd.length && overlap(keys.slice(-win), avoidEnd)) score++;
      if (score < bestScore) { best = order; bestScore = score; }
      if (score === 0) break;
    }
    var p = 0;
    return source.map(function (n) { return isPlatform(n) ? best[p++] : n; });
  }

  class J9Marketplaces extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.rows = Array.prototype.map.call(this.querySelectorAll('[data-j9mp-row]'), function (row, i) {
        var track = row.querySelector('[data-j9mp-track]');
        return {
          row: row,
          track: track,
          index: i,
          source: Array.prototype.map.call(track.children, function (n) { return n.cloneNode(true); })
        };
      });
      if (!this.rows.length || !this.rows[0].source.length) return;

      this.build();

      this._onResize = () => {
        clearTimeout(this._rt);
        this._rt = setTimeout(() => {
          if (Math.abs(this.clientWidth - this._width) > 2) this.build();
        }, 150);
      };
      window.addEventListener('resize', this._onResize);

      // Re-measure once logos have loaded (widths can change)
      this._onImg = () => {
        clearTimeout(this._it);
        this._it = setTimeout(() => this.build(), 100);
      };
      this.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', this._onImg, { once: true });
      });

      // Pause when off screen (saves battery / CPU)
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          entries.forEach((e) => this.classList.toggle('is-offscreen', !e.isIntersecting));
        });
        this.io.observe(this);
      }

      // Theme editor: pause while a block is selected
      this._onSelect = (e) => { if (this.closest('.shopify-section') && this.closest('.shopify-section').contains(e.target)) this.classList.add('is-editing'); };
      this._onDeselect = () => this.classList.remove('is-editing');
      document.addEventListener('shopify:block:select', this._onSelect);
      document.addEventListener('shopify:block:deselect', this._onDeselect);
    }

    disconnectedCallback() {
      this._ready = false;
      if (this.io) this.io.disconnect();
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      document.removeEventListener('shopify:block:select', this._onSelect);
      document.removeEventListener('shopify:block:deselect', this._onDeselect);
      clearTimeout(this._rt);
      clearTimeout(this._it);
    }

    build() {
      this._width = this.clientWidth;
      var motion = this.dataset.motion || 'always';
      var shuffleOn = this.dataset.shuffle === 'true';
      var speed = parseFloat(this.dataset.speed) || 40;

      this.rows.forEach((r) => {
        // 1. Reset to one plain set and measure it
        r.row.classList.remove('is-static', 'is-moving');
        r.track.style.removeProperty('--j9mp-dur');
        r.track.replaceChildren.apply(r.track, r.source.map(function (n) { return n.cloneNode(true); }));

        var viewW = r.row.clientWidth;
        var setW = r.track.scrollWidth;

        var move = !reduceMotion && motion !== 'never' && (motion === 'always' || setW > viewW + 1);
        if (!move) {
          r.row.classList.add('is-static');
          return;
        }

        // 2. How many copies fill the screen (plus variety when shuffling)
        var copies = Math.max(1, Math.ceil(viewW / Math.max(setW, 1)));
        if (shuffleOn) copies = Math.max(copies, 3);

        // 3. Build the sequence: copy 1 keeps your order on row 1; others are shuffled
        var rand = seeded(97 + r.index * 131);
        var nPlat = r.source.filter(isPlatform).length;
        var win = nPlat >= 5 ? 2 : (nPlat >= 2 ? 1 : 0);
        var seq = [];
        for (var c = 0; c < copies; c++) {
          var keepOrder = !shuffleOn || (c === 0 && r.index === 0);
          var part;
          if (keepOrder) {
            part = r.source.slice();
          } else {
            var soFar = platformKeys(seq);
            var avoidStart = win ? soFar.slice(-win) : [];
            var avoidEnd = (c === copies - 1 && win) ? soFar.slice(0, win) : [];
            part = permute(r.source, rand, avoidStart, avoidEnd, win);
          }
          seq = seq.concat(part);
        }

        // 4. Second row starts half a set later, so text items and logos don't line up with row 1
        if (r.index > 0 && seq.length > 1) {
          var shift = Math.floor(r.source.length / 2);
          seq = seq.slice(shift).concat(seq.slice(0, shift));
          // …and never opens with the same logo as row 1
          for (var g = 0; g < seq.length && this._firstKey && platformKeys(seq)[0] === this._firstKey; g++) {
            seq = seq.slice(1).concat(seq.slice(0, 1));
          }
        }
        if (r.index === 0) this._firstKey = platformKeys(seq)[0];

        // 5. Render sequence twice (seamless -50% loop); hide duplicates from screen readers / keyboard
        var frag = document.createDocumentFragment();
        var seen = new Set();
        var self = this;
        [false, true].forEach(function (secondHalf) {
          seq.forEach(function (n) {
            var node = n.cloneNode(true);
            var key = keyOf(node);
            if (secondHalf || r.index > 0 || seen.has(key)) self.hideClone(node);
            else seen.add(key);
            frag.appendChild(node);
          });
        });
        r.track.replaceChildren(frag);

        var half = r.track.scrollWidth / 2;
        r.track.style.setProperty('--j9mp-dur', Math.max(half / speed, 4).toFixed(2) + 's');
        r.row.classList.add('is-moving');
      });
    }

    hideClone(node) {
      node.setAttribute('aria-hidden', 'true');
      node.removeAttribute('data-shopify-editor-block');
      node.querySelectorAll('a, button').forEach(function (el) { el.setAttribute('tabindex', '-1'); });
    }
  }

  customElements.define('j9-marketplaces', J9Marketplaces);
})();